import { AnimationClip, ObjectLoader, TextureLoader } from 'three'
import type { Camera, DataTexture, Group, Loader, LoadingManager, Object3D, Texture } from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import localforage from 'localforage'
import { RGBELoader } from 'three-stdlib'
import type { Document } from '@gltf-transform/core'
import { WebIO } from '@gltf-transform/core'
import { KHRONOS_EXTENSIONS } from '@gltf-transform/extensions'
import { dedup, flatten, instance, join, metalRough, palette, prune, resample, simplify, sparse, textureCompress, weld } from '@gltf-transform/functions'
import { MeshoptSimplifier } from 'meshoptimizer'
import { GLTFLoader } from '../../extend/GLTFLoader'
import { TextureDeduplicator, runtimeCache } from './Cache'

type BaseOptions = {
  url: string
  onProgress?: (event: ProgressEvent) => void
  onError?: (event: ErrorEvent) => void
  customLoadingManager?: LoadingManager
}

type LoadGLTFOptions = {
  isCache?: boolean
  isBackward?: boolean
  autoBackward?: boolean
  onLoad?: (gltf: GLTF) => void
  draco?: boolean
  dracoPath?: string
  simplifierRatio?: number
} & BaseOptions

type loadGLTFToBuffer = {
  isBackward?: boolean
  autoBackward?: boolean
  draco?: boolean
  dracoPath?: string
  simplifierRatio?: number
  url: string
}

type LoadTextureOptions = {
  onLoad?: (texture: Texture) => void
} & BaseOptions

type SerializedType = {
  position: Object3D['position']
  rotation: Object3D['rotation']
  scale: Object3D['scale']
  children?: SerializedType[]
}

type LoadFBXOptions = {
  isCache?: boolean
  onLoad?: (scene: Group) => void
} & BaseOptions

type SceneSerializeObject = {
  scene: any
  geometryInfo: SerializedType
}

const deduplicator = new TextureDeduplicator()

/**
 * @file LoaderModule
 * main solve
 * 1. 统一load使用
 * 2. gltf load资源缓存 「1.资源可以通过http缓存 2.gltf解析结果缓存」
 * 3. load进度规范管理
 * 4. 批量glb 多线程预加载 todo
 */

class LoaderModule {
  private gltfLoader!: GLTFLoader
  private dracoLoader!: DRACOLoader
  constructor() { }

  cleanCache() {
    localforage.clear()
  }

  /**
   * load GLTF To Buffer
   * @param args
   * @returns
   */
  async loadGLTFToBuffer(args: loadGLTFToBuffer): Promise<ArrayBuffer> {
    const { url, draco, dracoPath, isBackward = false, autoBackward = false, simplifierRatio } = args
    if (!this.gltfLoader)
      this.gltfLoader = new GLTFLoader(undefined)
    this.setupDracoLoader(dracoPath)

    let document: Document | null = null
    let io: WebIO | null = null
    let currentIsBackward = isBackward

    if (autoBackward || isBackward) {
      try {
        io = new WebIO().registerExtensions(KHRONOS_EXTENSIONS)
        document = await io.read(url)
        currentIsBackward = true
      }
      catch (error) {
        currentIsBackward = false
        if (isBackward)
          throw error
      }
    }

    const load = async (res: (value: ArrayBuffer) => void, rej: (reason?: any) => void) => {  
      try {
        if (currentIsBackward && io && document) {
          await document.transform(metalRough())
          await document.transform(
            dedup(),
            instance({ min: 5 }),
            palette({ min: 5 }),
            flatten(),
            sparse({ ratio: 0.2 }),
            prune(),
          )
          await document.transform(
            textureCompress({ targetFormat: 'webp', resize: [1024, 1024] }),
          )
          const transformGlb = await io.writeBinary(document)

          res(transformGlb.buffer as ArrayBuffer)
        }
        else if (simplifierRatio) {
          const io = new WebIO().registerExtensions(KHRONOS_EXTENSIONS)
          const document = await io.read(url)
          await document.transform(metalRough())
          await document.transform(
            dedup(),
            flatten(),
            instance({ min: 5 }),
            palette({ min: 5 }),
            textureCompress({ targetFormat: 'webp', resize: [1024, 1024] }),
            simplify({
              simplifier: MeshoptSimplifier,
              error: 0.0001,
              ratio: simplifierRatio,
              lockBorder: true,
            }),
          )

          const transformGlb = await io.writeBinary(document)
          res(transformGlb.buffer as ArrayBuffer)
        }
        else {
          const io = new WebIO().registerExtensions(KHRONOS_EXTENSIONS)
          const document = await io.read(url)
          await document.transform(metalRough())
          const transformGlb = await io.writeBinary(document)

          res(transformGlb.buffer as ArrayBuffer)
        }
      }
      catch (error) {
        rej(error)
      }
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (res, rej) => {
      load(res, rej)
    })
  }

  /**
   * 初始化或更新DRACO解码器
   * @param decoderPath DRACO解码器路径
   */
  private setupDracoLoader(decoderPath: string | undefined) {
    if (!this.dracoLoader) {
      this.dracoLoader = new DRACOLoader()
      this.dracoLoader.setDecoderPath(decoderPath || '/draco/')
      this.dracoLoader.setDecoderConfig({ type: 'js' })
      this.dracoLoader.preload()
      this.gltfLoader.setDRACOLoader(this.dracoLoader)
    }
    // 更新解码器路径（如果与当前路径不同）
    if (decoderPath)
      this.dracoLoader.setDecoderPath(decoderPath)
  }

  /**
   * load gltf
   * note: isBackward = true , onProgress is not work
   * TODO 资源缓存保持和http缓存具有一致的清除机制 「但是单方面目前做不到，库项目使用Service Worker目前不太友好」
   * @param args
   * @returns
   */
  async loadGLTF(args: LoadGLTFOptions): Promise<GLTF> {
    const { url, isCache, draco, onLoad, onProgress, onError, dracoPath, customLoadingManager, isBackward = false, autoBackward = false, simplifierRatio } = args
    if (!this.gltfLoader)
      this.gltfLoader = new GLTFLoader(undefined)
    this.setupDracoLoader(dracoPath)

    // ── 情况1：运行时缓存命中，直接 clone ──────────────────────
    // 同一 url 第2次及以后都走这里，零开销
    if (runtimeCache.has(url)) {
      const gltf = runtimeCache.clone(url)! as any
      onLoad && onLoad(gltf)
      onProgress && onProgress({ loaded: 1, total: 1 } as any) // 返回进度
      return gltf
    }

    // ── 情况2：并发请求同一 url，等待第一个加载完再 clone ───────
    if (runtimeCache.getPending(url)) {
      await runtimeCache.getPending(url)
      const gltf = runtimeCache.clone(url)! as any
      onLoad && onLoad(gltf)
      onProgress && onProgress({ loaded: 1, total: 1 } as any) // 返回进度
      return gltf
    }

    // ── 情况3：首次加载，走 localforage 或网络 ──────────────────
    let resolvePending!: () => void
    const pendingPromise = new Promise<void>((r) => {
      resolvePending = r
    })
    runtimeCache.setPending(url, pendingPromise)

    let document: Document | null = null
    let io: WebIO | null = null
    let currentIsBackward = isBackward

    if (autoBackward || isBackward) {
      try {
        io = new WebIO().registerExtensions(KHRONOS_EXTENSIONS)
        document = await io.read(url)
        currentIsBackward = true
      }
      catch (error) {
        currentIsBackward = false
        if (isBackward)
          throw error
      }
    }

    const load = async (res: (value: GLTF | PromiseLike<GLTF>) => void, rej: (reason?: any) => void) => {
      if (currentIsBackward && io && document) {
        await document.transform(metalRough())
        await document.transform(
          dedup(),
          instance({ min: 5 }),
          palette({ min: 5 }),
          flatten(),
        )
        await document.transform(
          textureCompress({ targetFormat: 'webp', resize: [1024, 1024] }),
        )
        const transformGlb = await io.writeBinary(document)

        this.gltfLoader.parse(transformGlb.buffer, '', async (gltf: GLTF) => {
          onLoad && onLoad(gltf)
          gltf.scene.updateMatrixWorld()
          // 纹理去重
          await deduplicator.run(gltf.scene)
          localforage.setItem(`${url}data`, gltf.scene.toJSON())
          res(gltf)
        },
        (error: any) => {
          onError && onError(error)
          rej(error)
        })
      }
      else if (simplifierRatio) {
        const io = new WebIO().registerExtensions(KHRONOS_EXTENSIONS)
        const document = await io.read(url)
        await document.transform(metalRough())
        await document.transform(
          dedup(),
          flatten(),
          instance({ min: 5 }),
          palette({ min: 5 }),
          textureCompress({ targetFormat: 'webp', resize: [1024, 1024] }),
          simplify({
            simplifier: MeshoptSimplifier,
            error: 0.0001,
            ratio: simplifierRatio,
            lockBorder: true,
          }),
        )

        const transformGlb = await io.writeBinary(document)

        this.gltfLoader.parse(transformGlb.buffer, '', async (gltf: GLTF) => {
          onLoad && onLoad(gltf)
          gltf.scene.updateMatrixWorld()
          // 纹理去重
          await deduplicator.run(gltf.scene)

          localforage.setItem(`${url}data`, gltf.scene.toJSON())
          res(gltf)
        },
        (error: any) => {
          onError && onError(error)
          rej(error)
        })
      }
      else {
        this.gltfLoader.load(url, async (gltf: GLTF) => {
          gltf.scene.updateMatrixWorld()
          // 纹理去重
          await deduplicator.run(gltf.scene)
          runtimeCache.set(url, gltf.scene, gltf.parser.json.meshes)

          localforage.setItem(`${url}data`, gltf.scene.toJSON())
          localforage.setItem(`${url}meshes`, gltf.parser.json.meshes)
          resolvePending()
          runtimeCache.deletePending(url)
          const cloned = runtimeCache.clone(url) as any
          onLoad && onLoad(cloned)
          res(cloned)
        },
        onProgress,
        (error: any) => {
          resolvePending() // 失败也要 resolve，否则并发请求永远等待
          runtimeCache.deletePending(url)
          onError && onError(error)
          rej(error)
        })
      }
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (res, rej) => {
      if (isCache) {
        const cachedData = await localforage.getItem(`${url}data`) as any

        if (cachedData) {
          const loader = new ObjectLoader()
          const sceneObject = await loader.parseAsync(cachedData)
          const serializedGLTFMeshes = await localforage.getItem(`${url}meshes`)

          console.log('load from cache', url)
          await deduplicator.run(sceneObject)
          runtimeCache.set(url, sceneObject, serializedGLTFMeshes!)
          resolvePending()
          runtimeCache.deletePending(url)

          onProgress && onProgress({
            loaded: 1,
            total: 1,
            type: 'progress',
          } as ProgressEvent)
          const gltf = runtimeCache.clone(url) as any as GLTF
          onLoad && onLoad(gltf)

          res(gltf)
        }
        else {
          load(res, rej)
        }
      }
      else {
        load(res, rej)
      }
    })
  }

  /**
   * load fbx
   * @param args
   * @returns
   */
  async loadFbx(args: LoadFBXOptions): Promise<Group> {
    const { url, isCache, onLoad, onProgress, onError, customLoadingManager } = args
    const fbxLoader = new FBXLoader()

    const load = (res: (value: Group | PromiseLike<Group>) => void, rej: (reason?: any) => void) => {
      fbxLoader.load(url, (scene) => {
        onLoad && onLoad(scene)
        scene.updateMatrixWorld()
        localforage.setItem(`${url}data`, scene.toJSON())

        res(scene)
      }, onProgress, (error) => {
        onError && onError(error as ErrorEvent)
        rej(error)
      })
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (res, rej) => {
      if (isCache) {
        const serializedFBX = await localforage.getItem(`${url}data`)

        if (serializedFBX) {
          const loader = new ObjectLoader()
          const sceneObject = await loader.parseAsync(serializedFBX)

          console.log('load from cache', url)

          onLoad && onLoad(sceneObject as Group)

          res(sceneObject as Group)
        }
        else {
          load(res, rej)
        }
      }
      else {
        load(res, rej)
      }
    })
  }

  /**
   * load texture
   * @param args
   * @returns
   */
  async loadTexture(args: LoadTextureOptions): Promise<Texture> {
    const { url, onLoad, onProgress, onError, customLoadingManager } = args
    const textureLoader = new TextureLoader(customLoadingManager)

    return new Promise((res, rej) => {
      textureLoader.load(url,
        (texture) => {
          onLoad && onLoad(texture)
          res(texture)
        },
        onProgress,
        (error) => {
          onError && onError(error as ErrorEvent)
          rej(error)
        })
    })
  }

  /**
   * loadRgbe
   * @param args
   * @returns
   */
  loadRgbe(args: LoadTextureOptions): Promise<DataTexture> {
    const { url, onLoad, onProgress, onError, customLoadingManager } = args
    const loader = new RGBELoader(customLoadingManager)

    return new Promise((res, rej) => {
      loader.load(url,
        (texture) => {
          onLoad && onLoad(texture)
          res(texture)
        },
        onProgress,
        (error) => {
          onError && onError(error as ErrorEvent)
          rej(error)
        })
    })
  }

  // 字体
  extend(load: Loader) {
    return load
  }
}

export default LoaderModule
